/**
 * Helpers to surface the REAL reason behind a failed request.
 *
 * The registration forms used to render a hardcoded "Please complete the fields"
 * message for every kind of failure (client side validation AND API errors),
 * which hid the actual cause: 409 duplicate email, 422 invalid data, 5xx server
 * error, network failure... These helpers keep the API message intact so the
 * user can act on it.
 */

export const API_ERROR_MESSAGES = {
  400: "The submitted data is invalid. Please review the form and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You do not have permission to perform this action.",
  404: "The requested resource was not found.",
  409: "A record with the same email is already registered.",
  422: "The data could not be processed. Please review the form and try again.",
  500: "The server could not complete the request. Please try again.",
};

export const NETWORK_ERROR_MESSAGE =
  "Could not reach the server. Check your connection and try again.";

/**
 * Extracts a human readable message from an Axios error.
 *
 * @param {*} error the rejected value coming from Axios
 * @param {string} fallback message used when nothing useful can be extracted
 * @returns {string}
 */
export const extractApiError = (error, fallback = "") => {
  if (!error) return fallback;

  const response = error.response;
  const data = response && response.data;
  const status = response && response.status;

  // { message: "..." } -> business errors raised by the API (409, 422, ...)
  if (data && typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }

  // { error: "..." } -> plain string errors
  if (data && typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }

  // { error: { ... } } -> serialized driver errors, never user friendly as is
  if (data && data.error && typeof data.error === "object") {
    if (data.error.name === "SequelizeForeignKeyConstraintError") {
      return "This record points to data that no longer exists. Please contact support.";
    }
    if (data.error.name === "SequelizeUniqueConstraintError") {
      return "A record with the same email is already registered.";
    }
  }

  if (status && API_ERROR_MESSAGES[status]) {
    return API_ERROR_MESSAGES[status];
  }

  if (!response) {
    return NETWORK_ERROR_MESSAGE;
  }

  return fallback || `Unexpected error (HTTP ${status}).`;
};

/**
 * True when the request was rejected because the email is already registered.
 *
 * @param {*} error
 * @returns {boolean}
 */
export const isDuplicateEmailError = (error) =>
  !!error && error.response && error.response.status === 409;

/**
 * Returns the keys of the required fields that are still empty.
 *
 * @param {Array<{key: string, label: string, value: any}>} fields
 * @returns {string[]} the missing field keys
 */
export const getMissingFieldKeys = (fields) =>
  fields
    .filter(
      (field) =>
        !String(
          field.value === undefined || field.value === null ? "" : field.value
        ).trim()
    )
    .map((field) => field.key);

/**
 * Returns the labels of the required fields that are still empty.
 *
 * @param {Array<{key: string, label: string, value: any}>} fields
 * @returns {string[]} the missing field labels
 */
export const getMissingFieldLabels = (fields) =>
  fields
    .filter((field) => getMissingFieldKeys([field]).length > 0)
    .map((field) => field.label);
